import { isEmpty, isNil } from "lodash";
import {
    BOOLEAN_OPTIONS,
    EXTENSIONS_CONTENT_TYPE,
    FIELD_TYPES,
} from "./constants";
import i18next, { t } from "i18next";

const addKeyIfExists = (obj, key, targetObj) => {
    if (obj[key]) {
        if (targetObj[key]) {
            targetObj[key] = targetObj[key].concat(obj[key]);
        } else {
            targetObj[key] = [obj[key]];
        }
    } else {
        if (targetObj[key]) {
            targetObj[key] = targetObj[key].concat(null);
        } else {
            targetObj[key] = [null];
        }
    }
};

const extractFileName = (filename) => {
    const lastDotIndex = filename.lastIndexOf(".");
    if (lastDotIndex === -1) {
        return filename;
    } else {
        return filename.substring(0, lastDotIndex);
    }
};

const getBooleanNullValue = (value) => {
    value = value?.toString();

    switch (value) {
        case BOOLEAN_OPTIONS.true:
            return true;
        case BOOLEAN_OPTIONS.false:
            return false;
        default:
            return null;
    }
};

const trimValues = (object) => {
    let _values = {};
    if (object) {
        Object.keys(object).forEach((key) => {
            if (
                typeof object[key] === "object" ||
                typeof object[key] === "boolean"
            )
                _values[key] = object[key];
            else if (!isNil(object[key])) {
                _values[key] = object[key] ? object[key].trim() : null;
            }
        });
    }
    return _values;
};

const classNames = (...args) => {
    return args.filter(Boolean).join(" ");
};

const objectValuesFromNumbersToString = (object) => {
    if (typeof object !== "object" || isEmpty(object)) return {};
    Object.keys(object).forEach((k) => {
        if (typeof object[k] === "object") {
            return toString(object[k]);
        }
    });
    return object;
};

const getEnvironment = () => {
    const baseURL = process.env.REACT_APP_API_URL.replace("https://", "");
    const splittedURL = baseURL.split(".");
    return splittedURL.length > 2 ? splittedURL[0] : "dev";
};

const removeUnchangedProps = (existingData, newData, skipProps = []) => {
    const modifiedData = { ...newData };
    Object.keys(newData).forEach((key) => {
        if (modifiedData[key] === existingData[key] && !skipProps.includes(key))
            delete modifiedData[key];
    });
    return modifiedData;
};

const splitValueUnit = (param) => {
    let value;
    if (param && (typeof param === "string" || param instanceof String)) {
        const unit =
            param
                .trim()
                .split(/\d+/g)
                .filter((n) => n)
                .pop()
                ?.trim() || null;
        if (unit) {
            value = param
                .trim()
                .split(unit)
                .filter((n) => n)[0]
                ?.trim();
        }
    }
    return value || param || 0;
};

const toCamelCase = (str) => {
    return (
        str &&
        str
            .replace(/\s(.)/g, (a) => {
                return a.toUpperCase();
            })
            .replace(/\s/g, "")
            .replace(/^(.)/, (b) => {
                return b.toLowerCase();
            })
    );
};

const kebabToCamelCase = (str) => {
    if (str && typeof str === String) {
        const arr = str.split("-");
        const capital = arr.map((item, index) =>
            index
                ? item.charAt(0).toUpperCase() + item.slice(1).toLowerCase()
                : item
        );
        const capitalString = capital.join("");

        return capitalString;
    }
    return "";
};

const formResetHandler = (fields) => {
    const fieldsObj = {};
    const newInputObj = {};
    fields.map((elem) => (fieldsObj[elem.name] = elem));

    Object.keys(fieldsObj).map((key) => {
        if (!fieldsObj[key]?.role) {
            const type = fieldsObj[key].type;
            switch (type) {
                case FIELD_TYPES.Input:
                    newInputObj[key] = "";
                    return;
                case FIELD_TYPES.Dropdown:
                    newInputObj[key] = fieldsObj[key].isMulti ? [] : null;
                    return;
                default:
                    newInputObj[key] = null;
                    return;
            }
        }
    });
    return newInputObj;
};

const toBemCase = (str) => {
    if (str) {
        if (str.includes(" ")) return str.replace(" ", "-")?.toLowerCase();
        // converting to bem-case from camelCase
        else return str.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
    }
};

//capitalize only the first letter of the string.
const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};
//capitalize all words of a string.
const capitalizeWords = (string) => {
    if (!string) return;
    return string.replace(/(?:^|\s)\S/g, (a) => a.toUpperCase());
};

const mapLookupToSelect = (data, nameAccessor = "name", idAccessor = "id") => {
    return data?.map((record) => ({
        value: record[idAccessor],
        label: record[nameAccessor],
        ...record,
    }));
};

const stringifyIfNumber = (val) => {
    return typeof val === "number" ? val.toString() : val;
};

const getEncodedValue = (name) => {
    return name?.replaceAll("%", "__percent_sign__");
};

const getNameWithoutExtension = (fileName) => {
    return fileName?.substring(0, fileName?.lastIndexOf("."));
};

const validateEmail = (email) => {
    return String(email)
        .toLowerCase()
        .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
};

const handleTranslation = (translations, fallback) =>
    translations?.[i18next.language] || fallback;

const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

const getValueByPath = (obj, path) => {
    const keys = path.split(".");
    let result = obj;
    for (const key of keys) {
        if (result && Object.hasOwn(result, key)) {
            result = result[key];
        } else {
            return undefined; // Property not found
        }
    }
    return result;
};

const removeLastPartitionFromRoute = (route) => {
    return route.replace(/\/[^/]+$/, "");
};

//Recursive function that handles translating label keys in objects
const handleLabelTranslation = (obj) => {
    if (Array.isArray(obj)) {
        return obj.map((elem) => handleLabelTranslation(elem));
    } else {
        let newObj = obj;
        for (const key in obj) {
            if (key === "label") {
                return {
                    ...newObj,
                    label: t(newObj.label),
                    ...(newObj.placeHolder && {
                        placeHolder: t(newObj.placeHolder),
                    }),
                };
            } else if (
                typeof obj[key] === "object" &&
                !Array.isArray(obj[key]) &&
                obj[key] !== null
            ) {
                newObj = {
                    ...newObj,
                    [key]: handleLabelTranslation(newObj[key]),
                };
            }
        }
        return newObj;
    }
};

const getURIParts = (partIndex = 0) => {
    const parts = window.location.pathname.split("/");
    parts.shift();

    return parts?.[partIndex];
};

const splitLastOccurrence = (str, substring) => {
    const lastIndex = str.lastIndexOf(substring);

    const before = str.slice(0, lastIndex);

    const after = str.slice(lastIndex + 1);

    return [before, after];
};

const getUniqueObjectsByKey = (arr, key) => {
    const existingMap = {};
    const newArr = [];

    arr.forEach((obj) => {
        if (!existingMap[obj[key]]) {
            existingMap[obj[key]] = true;
            newArr.push(obj);
        }
    });

    return newArr;
};

const handleXHRContentTypeHeader = (file) => {
    const path = file.path;
    const [, extension] = splitLastOccurrence(path, ".");
    return EXTENSIONS_CONTENT_TYPE[`.${extension.toLowerCase()}`] || "";
};

const getOffsetAfterDeletion = (currentPage, items) => {
    let newCurrentPage = currentPage;
    if (items.length <= 1 && currentPage > 1) {
        newCurrentPage = currentPage - 1;
    }

    return newCurrentPage - 1;
};

String.prototype.fillRoute = function (params) {
    let route = this;

    if (!isEmpty(params)) {
        Object.keys(params).forEach((key) => {
            const formattedKey = `:${key}`;
            if (route.includes(formattedKey)) {
                route = route.replace(formattedKey, params[key]);
            }
        });
    }
    return route;
};

String.prototype.customToUpperCase = function () {
    let str = this;
    return str?.replace(/[a-z]/g, (match) =>
        String.fromCharCode(match.charCodeAt(0) - 32)
    );
};

String.prototype.addQueryParams = function (params) {
    let route = this;

    if (!isEmpty(params)) {
        route += "?";
        Object.keys(params).forEach((key) => {
            if (!isNil(params[key])) route += `${key}=${params[key]}&`;
        });
        route = route.slice(0, -1);
    }

    return route;
};

export {
    formatFileSize,
    addKeyIfExists,
    trimValues,
    getEnvironment,
    classNames,
    splitValueUnit,
    objectValuesFromNumbersToString,
    removeUnchangedProps,
    toCamelCase,
    toBemCase,
    capitalizeFirstLetter,
    extractFileName,
    capitalizeWords,
    mapLookupToSelect,
    stringifyIfNumber,
    getEncodedValue,
    getNameWithoutExtension,
    formResetHandler,
    validateEmail,
    handleTranslation,
    getValueByPath,
    getBooleanNullValue,
    removeLastPartitionFromRoute,
    handleLabelTranslation,
    kebabToCamelCase,
    getURIParts,
    splitLastOccurrence,
    getUniqueObjectsByKey,
    handleXHRContentTypeHeader,
    getOffsetAfterDeletion,
};
